import { CloseCircleOutlined } from '@ant-design/icons';
import { FocusEventHandler } from 'react';
import SingleSelect, {
  ActionMeta,
  ClearIndicatorProps,
  DropdownIndicatorProps,
  GroupBase,
  InputActionMeta,
  OptionsOrGroups,
  SingleValue,
} from 'react-select';
import { SelectComponentsConfig } from 'react-select/dist/declarations/src/components';

import DropdownIndicator from '../DropdownIndicator';
import Loader, { LoaderSize } from '../Loader';
import { Option } from '../types';
import brunoTheme from './brunoTheme';
import { Description, Label, StyledContainer } from './styled';
import theme from './theme';
import { filterOption, filterOptionOnBE } from './utils';

const customComponents = {
  DropdownIndicator: ({ selectProps }: DropdownIndicatorProps<Option, false, GroupBase<Option>>): JSX.Element => (
    <DropdownIndicator dataTestId="dropdownIndicator" active={selectProps.menuIsOpen} />
  ),
  LoadingMessage: (): null => null,
  LoadingIndicator: (): JSX.Element => <Loader size={LoaderSize.medium} />,
  ClearIndicator: (props: ClearIndicatorProps<Option, false, GroupBase<Option>>): JSX.Element => {
    const {
      innerProps: { ref, ...restInnerProps },
    } = props;

    return (
      <div {...restInnerProps} ref={ref}>
        <CloseCircleOutlined data-testid="clearSelectValueIndicator" />
      </div>
    );
  },
};

export const classNames = {
  control: (): string => 'control',
  valueContainer: (): string => 'valueContainer',
  menuList: (): string => 'menuList',
  option: (): string => 'option',
};

type Props = {
  options?: OptionsOrGroups<Option, GroupBase<Option>>;
  value?: SingleValue<Option>;
  isLoading?: boolean;
  onSearch?: (newValue: string, actionMeta: InputActionMeta) => void;
  onChange?: (newValue: SingleValue<Option>, actionMeta: ActionMeta<Option>) => void;
  searchTerm?: string;
  onScrollToBottom?: (event: WheelEvent | TouchEvent) => void;
  label?: string;
  placeholder?: string;
  dataTestId?: string;
  defaultValue?: SingleValue<Option>;
  isClearable?: boolean;
  hideSelectedOptions?: boolean;
  isSearchable?: boolean;
  className?: string;
  description?: string;
  isDisabled?: boolean;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  error?: boolean;
  components?: SelectComponentsConfig<Option, false, GroupBase<Option>>;
  isFilter?: boolean;
  isBrunoTheme?: boolean;
  isBESearch?: boolean;
  openMenuOnClick?: boolean;
};

const Select = ({
  options,
  value,
  isLoading = false,
  onSearch,
  onChange,
  searchTerm,
  onScrollToBottom,
  label,
  placeholder,
  dataTestId = 'select',
  defaultValue,
  isClearable = false,
  hideSelectedOptions,
  isSearchable = true,
  className,
  description,
  isDisabled = false,
  onBlur,
  onFocus,
  error,
  components,
  isFilter = false,
  isBrunoTheme = false,
  isBESearch,
  openMenuOnClick = true,
}: Props): JSX.Element => {
  const handleSearch = (newValue: string, actionMeta: InputActionMeta): string | undefined => {
    if (actionMeta.action === 'input-change') {
      onSearch?.(newValue, actionMeta);
      return newValue;
    }

    if (actionMeta.action === 'set-value') {
      onSearch?.('', actionMeta);
      return newValue;
    }

    return searchTerm;
  };

  return (
    <StyledContainer data-testid={dataTestId} $error={error} $isFilter={isFilter}>
      {label && <Label htmlFor={label}>{label}</Label>}
      {description && <Description>{description}</Description>}
      <SingleSelect
        openMenuOnClick={openMenuOnClick}
        onBlur={onBlur}
        onFocus={onFocus}
        styles={isBrunoTheme ? brunoTheme : theme}
        inputId={label}
        options={options}
        value={defaultValue || value}
        isLoading={isLoading}
        onInputChange={handleSearch}
        onChange={onChange}
        inputValue={searchTerm}
        onMenuScrollToBottom={onScrollToBottom}
        placeholder={placeholder}
        components={{ ...customComponents, ...components }}
        filterOption={isBESearch ? filterOptionOnBE : filterOption}
        isClearable={isClearable}
        defaultValue={defaultValue}
        classNames={classNames}
        hideSelectedOptions={hideSelectedOptions}
        isSearchable={isSearchable}
        className={className}
        isDisabled={isDisabled}
      />
    </StyledContainer>
  );
};

export default Select;
